import { useMutation, useQuery } from '@tanstack/react-query';

import {
  REPORTING_LE_LENS_CATALOGUE_PATH,
  REPORTING_LE_LENS_CATALOGUE_SEARCH_PATH,
  REPORTING_LE_LENS_COMPOSE_PATH,
  reportingLeLensCataloguePath,
} from '~/consts/reportingLeLensApi';

import { request } from '~/services/common';

import type {
  LensCatalogueDataset,
  LensCatalogueListResponseBody,
  LensCatalogueSearchResponseBody,
  LensComposeRequestBody,
  LensComposeResponseBody,
} from '~/types/leLens';

import { resolveReportingLeLensUrl } from '~/utils/resolveReportingLeLensUrl';

export const lensCatalogueQueryKeys = {
  all: ['reporting', 'le-lens', 'catalogue'] as const,
  detail: (catalogueId: string) => [...lensCatalogueQueryKeys.all, catalogueId] as const,
  search: (query: string) => [...lensCatalogueQueryKeys.all, 'search', query] as const,
};

/** Every governed dataset a person can anchor a Lens on. Changes when the data team ships one, not per click. */
export function useGetLensCatalogue(enabled = true) {
  return useQuery({
    enabled,
    queryFn: async () =>
      (await request(resolveReportingLeLensUrl(REPORTING_LE_LENS_CATALOGUE_PATH), {
        method: 'GET',
      })) as LensCatalogueListResponseBody,
    queryKey: lensCatalogueQueryKeys.all,
    staleTime: 5 * 60 * 1000,
  });
}

/** One governed dataset with its fields and a ready-to-save draft. */
export function useGetLensCatalogueDataset(catalogueId: string | undefined) {
  return useQuery({
    enabled: Boolean(catalogueId),
    queryFn: async () =>
      (await request(resolveReportingLeLensUrl(reportingLeLensCataloguePath(catalogueId as string)), {
        method: 'GET',
      })) as LensCatalogueDataset,
    queryKey: lensCatalogueQueryKeys.detail(catalogueId ?? ''),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * A request against a governed dataset becomes a card configuration, or a plain reason it cannot.
 * A mutation, not a query: asking again is a new act, and there is nothing to cache.
 */
export function useComposeLensCard() {
  return useMutation({
    mutationFn: async (body: LensComposeRequestBody) =>
      (await request(resolveReportingLeLensUrl(REPORTING_LE_LENS_COMPOSE_PATH), {
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
      })) as LensComposeResponseBody,
    retry: false,
  });
}

/** "Where do I find conversion rate?" answered with datasets and fields. Empty query, no request. */
export function useSearchLensCatalogue(query: string) {
  const trimmed = query.trim();
  return useQuery({
    enabled: trimmed.length > 1,
    queryFn: async () =>
      (await request(
        resolveReportingLeLensUrl(`${REPORTING_LE_LENS_CATALOGUE_SEARCH_PATH}?q=${encodeURIComponent(trimmed)}`),
        { method: 'GET' },
      )) as LensCatalogueSearchResponseBody,
    queryKey: lensCatalogueQueryKeys.search(trimmed),
    staleTime: 60 * 1000,
  });
}
